import { Button, toast } from "@cafe/ui";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Headset, Info } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useAuth } from "@/features/auth/store";
import { SupportAccessPrompt } from "@/features/support/access-prompt";
import { sessionKeys, useCan, useEntitlements } from "@/shared/hooks/session";
import { useRealtime } from "@/shared/realtime/socket";

const bar = "flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-sm font-medium";

/** Сеанс поддержки платформы (impersonation): режим и выход. Виден везде, где сотрудник поддержки может оказаться, — и вне оболочки (мастер настройки). */
export function SupportBanner() {
  const { t } = useTranslation();
  const imp = useAuth((s) => s.impersonation);
  const clear = useAuth((s) => s.clear);
  if (!imp) return null;
  return (
    <div role="status" className={`${bar} bg-info-soft text-info`}>
      <Headset className="size-4" aria-hidden />
      <span>{t(imp.mode === "read_only" ? "billing.supportReadOnly" : "billing.supportFull")}</span>
      <Button size="sm" variant="outline" onClick={() => { clear(); window.close(); }}>{t("billing.supportExit")}</Button>
    </div>
  );
}

const daysLeft = (iso: string | null | undefined) => (iso ? Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)) : null);

/**
 * Баннеры оболочки (ТЗ 03 §5): статус подписки (trial ≤ 3 дней, ожидает оплаты, только чтение) и сеанс поддержки.
 * Изменение подписки в Platform Admin приходит событием `entitlements.updated` — без перезагрузки (AC-SaaS-01).
 */
export function ShellBanners() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const can = useCan();
  const ent = useEntitlements();
  // FR-QR-004: гость за столом зовёт официанта или просит счёт
  useRealtime<{ number: string }>(["table.call_waiter", "table.request_bill"], (e) => toast.warning(t(e.type === "table.call_waiter" ? "site.callWaiter" : "site.requestBill", { n: e.data.number }), { duration: 15_000 }));
  // BR-116: остатки обновить; само уведомление (тост и колокольчик) — notification.created
  useRealtime("stock.low", () => void qc.invalidateQueries({ queryKey: ["stock"] }));
  useRealtime("entitlements.updated", () => {
    void qc.invalidateQueries({ queryKey: sessionKeys.entitlements });
    void qc.invalidateQueries({ queryKey: ["subscription"] });
  });
  const s = ent.data;
  const trial = s?.status === "trialing" ? daysLeft(s.trialEndsAt) : null;
  return (
    <>
      <SupportBanner />
      <SupportAccessPrompt />
      {trial !== null && trial <= 3 && (
        <div role="status" className={`${bar} bg-info-soft text-info`}>
          <Info className="size-4" aria-hidden />
          <span>{t("subscription.trialLeft", { count: trial })}</span>
          {can("subscription.view") && <Link className="underline" to="/settings/subscription">{t("billing.choosePlan")}</Link>}
        </div>
      )}
      {(s?.status === "past_due" || s?.status === "read_only") && (
        <div role="alert" className={`${bar} ${s.status === "read_only" ? "bg-danger-soft text-danger" : "bg-warning-soft text-warning"}`}>
          <AlertTriangle className="size-4" aria-hidden />
          <span>{t(s.status === "read_only" ? "billing.bannerReadOnly" : "billing.bannerPastDue")}</span>
          {can("subscription.view") && <Link className="underline" to="/settings/subscription">{t("billing.payNow")}</Link>}
        </div>
      )}
    </>
  );
}
